import * as z from 'zod';

import type {BrandTagFragment} from '@/modules/commerce/brand/query';

import type {ItemForPriceFragment} from '../item/query';

import type {EmbroideryOptionCoreFragment} from './query';

export const embroideryConfigurationSchema = z
	.object({
		collectionId: z.string(),
		embroideryKey: z.string(),
	})
	.nullable();

export type EmbroideryConfiguration = z.infer<typeof embroideryConfigurationSchema>;

export function getDefaultEmbroideryConfiguration(
	_option: EmbroideryOptionCoreFragment,
): EmbroideryConfiguration {
	return null; // "null" means "no embroidery selected"
}

export function getInitialEmbroideryConfiguration(
	option: EmbroideryOptionCoreFragment,
): EmbroideryConfiguration {
	const collection = option.embroideryCollections?.at(0);
	if (!collection) {
		return null;
	}

	const embroidery = collection.embroideries?.at(0);
	if (!embroidery) {
		return null;
	}

	return {
		collectionId: collection._id,
		embroideryKey: embroidery._key,
	};
}

type EmbroideryCollectionInOption = NonNullable<
	EmbroideryOptionCoreFragment['embroideryCollections']
>[number];

export function filterOptionEmbroideriesByBrandIds(
	option: EmbroideryOptionCoreFragment,
	brandIds: string[],
) {
	const embroideryCollections = option.embroideryCollections;

	const filteredCollections =
		brandIds && brandIds.length > 0 && embroideryCollections
			? embroideryCollections.reduce<EmbroideryCollectionInOption[]>(
					(collections, collection) => {
						const embroideries = collection.embroideries?.filter((embroidery) => {
							const brands = embroidery.brands ?? [];
							if (brands.length === 0) {
								// If there are no brands on the embroidery, we assume it's compatible with all brands.
								return true;
							}

							for (const brandId of brandIds ?? []) {
								if (brands.find((brand) => brand._id === brandId)) {
									return true;
								}
							}
							return false;
						}, []);

						if (embroideries && embroideries.length > 0) {
							collections.push({
								...collection,
								embroideries,
							});
						}
						return collections;
					},
					[],
				)
			: embroideryCollections;

	return {...option, embroideryCollections: filteredCollections};
}

export function embroideryConfigurationFromFormData(
	option: EmbroideryOptionCoreFragment,
	prefix: string,
	formData: FormData,
): EmbroideryConfiguration {
	let config: EmbroideryConfiguration | null = null;

	if (!option.available) {
		return config;
	}

	const embbroideryEnabled = formData.get(`${prefix}`) === 'enabled';
	if (!embbroideryEnabled) {
		return config;
	}

	config = getInitialEmbroideryConfiguration(option);

	const collectionAndEmbroidery = formData.get(`${prefix}[embroideryId]`);
	if (collectionAndEmbroidery && typeof collectionAndEmbroidery === 'string') {
		const [collectionId, embroideryKey] = collectionAndEmbroidery.split(':');
		if (collectionId && embroideryKey) {
			const configuredCollection = option.embroideryCollections?.find(
				(collection) => collection._id === collectionId,
			);
			if (configuredCollection) {
				const configuredEmbroidery = configuredCollection.embroideries?.find(
					(embroidery) => embroidery._key === embroideryKey,
				);

				if (configuredEmbroidery) {
					config = {
						collectionId,
						embroideryKey,
					};
				}
			}
		}
	}

	return config;
}

export function getItemsForEmbroideryConfiguration(
	option: EmbroideryOptionCoreFragment,
	configuration: EmbroideryConfiguration,
): ItemForPriceFragment[] {
	const items: ItemForPriceFragment[] = [];

	if (!configuration) {
		return items;
	}

	const {configuredEmbroidery} = getConfiguredEmbroidery(option, configuration);

	if (configuredEmbroidery && option.items && option.items.length > 0) {
		items.push(...option.items);
	}

	return items;
}

export function getConfiguredEmbroidery(
	option: EmbroideryOptionCoreFragment,
	configuration: EmbroideryConfiguration,
) {
	if (!option.available || !configuration) {
		return {
			configuredCollection: null,
			configuredEmbroidery: null,
		};
	}

	const configuredCollection = option.embroideryCollections?.find(
		(collection) => collection._id === configuration.collectionId,
	);
	const configuredEmbroidery = configuredCollection?.embroideries?.find(
		(embroidery) => embroidery._key === configuration.embroideryKey,
	);

	if (!configuredEmbroidery) {
		return {
			configuredCollection: null,
			configuredEmbroidery: null,
		};
	}

	return {
		configuredCollection,
		configuredEmbroidery,
	};
}

export function getAllEmbroideryOptionBrands(option: EmbroideryOptionCoreFragment) {
	const brands: BrandTagFragment[] = [];

	if (!option.available) {
		return brands;
	}

	for (const collection of option.embroideryCollections || []) {
		for (const embroidery of collection.embroideries || []) {
			const embroideryBrands = embroidery?.brands;
			if (embroideryBrands) {
				for (const brand of embroideryBrands) {
					const existingBrand = brands.find((b) => b._id === brand._id);
					if (!existingBrand) {
						brands.push(brand);
					}
				}
			}
		}
	}

	return brands;
}
